import React, { useState, useEffect } from "react";
import {
  getTransactionHistory,
  getPaymentHistory,
  getTeamCredits,
} from "../../services/billing";
import AddCreditModal from "../../components/common/modals/AddCredit";
import { formatDate } from "utils/function";
import classNames from "classnames";
import allImgPaths from "../../assets/images";

interface BillingTabProps {
  currentTeamId: string;
}

interface SortParams {
  field: string;
  order: "asc" | "desc";
}

interface Payment {
  id: string;
  date: string;
  invoice: string;
  amount: string;
  status: string;
}

interface Transaction {
  id: string;
  date: string;
  project: string;
  activity: string;
  name: string;
  teamMember: string;
  charge: string;
}

const sampleTransactionData = {
  credits: 100,
  transactions: [],
  payments: [],
};

const emptyTransactions: Transaction[] = [];
const emptyPayments: Payment[] = [];

const BillingTab: React.FC<BillingTabProps> = ({ currentTeamId }) => {
  // State for billing data
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [credits, setCredits] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [noTransactionsData, setNoTransactionsData] = useState(false);
  const [noPaymentsData, setNoPaymentsData] = useState(false);
  const [isAddCreditModalOpen, setIsAddCreditModalOpen] = useState(false);

  const [activeTab, setActiveTab] = useState<"payment" | "transaction">(
    "payment"
  );

  const [paymentSortParams, setPaymentSortParams] = useState<SortParams>({
    field: "date",
    order: "desc",
  });
  const [transactionSortParams, setTransactionSortParams] =
    useState<SortParams>({ field: "date", order: "desc" });

  const fetchTransactionHistory = async (
    teamId: string,
    sortParams?: SortParams
  ) => {
    if (!teamId) return;

    setLoading(true);
    setError(null);
    setNoTransactionsData(false);

    if (sortParams) {
      setTransactionSortParams(sortParams);
    }

    try {
      const sortField = sortParams?.field || transactionSortParams.field;
      const sortOrder = sortParams?.order || transactionSortParams.order;
      const response = await getTransactionHistory(
        teamId,
        sortField,
        sortOrder
      );

      if (response?.data && response.data.length > 0) {
        const formattedTransactions = response.data.map((transaction: any) => ({
          id: transaction?._id,
          date: formatDate(transaction?.createdAt) || "N/A",
          project: transaction?.project_id?.name || "Unknown Project",
          activity: transaction?.type || "Unknown Activity",
          name:
            transaction?.comparison_id?.name ||
            transaction?.patent_id?.patent_id,
          teamMember: transaction?.user_id?.name || "Unknown User",
          charge: `$${parseFloat(transaction?.total_cost || 0).toFixed(2)}`,
        }));

        setTransactions(formattedTransactions);
      } else {
        setTransactions([]);
        setNoTransactionsData(true);
      }
    } catch (err: any) {
      console.error("Error fetching transaction history:", err);
      setError(err.message || "Failed to fetch transaction data");
      setNoTransactionsData(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentHistory = async (
    teamId: string,
    sortParams?: SortParams
  ) => {
    if (!teamId) return;

    setPaymentLoading(true);
    setPaymentError(null);
    setNoPaymentsData(false);

    if (sortParams) {
      setPaymentSortParams(sortParams);
    }

    try {
      const sortField = sortParams?.field || paymentSortParams.field;
      const sortOrder = sortParams?.order || paymentSortParams.order;
      const response: any = await getPaymentHistory(
        teamId,
        sortField,
        sortOrder
      );

      if (response?.success && response?.data?.length > 0) {
        const formattedPayments = response?.data?.map((payment: any) => ({
          id: payment._id,
          date: formatDate(payment.createdAt) || "N/A",
          invoice:
            payment.transaction_id || `INV-${payment._id.substring(0, 8)}`,
          amount: `$${payment.amount.toFixed(2)}`,
          status: payment.status,
        }));

        setPayments(formattedPayments);
      } else {
        setPayments([]);
        setNoPaymentsData(true);
      }
    } catch (err: any) {
      console.error("Error fetching payment history:", err);
      setPaymentError(err.message || "Failed to fetch payment data");
      setNoPaymentsData(true);
    } finally {
      setPaymentLoading(false);
    }
  };

  const fetchTeamCredits = async (teamId: string) => {
    if (!teamId) return;

    try {
      const response: any = await getTeamCredits(teamId);
      if (response?.success && response?.data?.credits) {
        setCredits(response?.data?.credits);
      }
    } catch (err: any) {
      console.error("Error fetching team credits:", err);
    }
  };

  useEffect(() => {
    if (currentTeamId) {
      fetchTransactionHistory(currentTeamId);
      fetchPaymentHistory(currentTeamId);
      fetchTeamCredits(currentTeamId);
    }
  }, [currentTeamId]);

  const handleAddCreditSuccess = () => {
    fetchTeamCredits(currentTeamId);
    fetchPaymentHistory(currentTeamId);
  };

  const handlePaymentSort = (field: string) => {
    const newOrder =
      field === paymentSortParams.field && paymentSortParams.order === "asc"
        ? "desc"
        : "asc";
    const newSortParams = { field, order: newOrder as "asc" | "desc" };
    setPaymentSortParams(newSortParams);
    fetchPaymentHistory(currentTeamId, newSortParams);
  };

  const handleTransactionSort = (field: string) => {
    const newOrder =
      field === transactionSortParams.field &&
      transactionSortParams.order === "asc"
        ? "desc"
        : "asc";
    const newSortParams = { field, order: newOrder as "asc" | "desc" };
    setTransactionSortParams(newSortParams);
    fetchTransactionHistory(currentTeamId, newSortParams);
  };

  return (
    <div className="mt-5">
      {/* Credit Balance Section */}
      <div className="border border-green-500 rounded-lg p-6 mb-8">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-lg font-interSemiBold text-[#101828]">
              Current Balance:
              <span className="ml-2 font-interSemiBold text-[#1E0AF5] text-2xl">
                ${credits.toFixed(2)}
              </span>
            </p>
          </div>
          <button
            onClick={() => setIsAddCreditModalOpen(true)}
            className="submit-btn-blue !px-[15px] !text-sm hover:bg-[#120693]"
          >
            Add Credits
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mt-8 mb-6">
        <div className="flex justify-between items-center">
          <ul className="flex items-center gap-x-4">
            <li
              onClick={() => setActiveTab("payment")}
              className={classNames(
                "block text-sm font-interSemiBold hover:text-[#1E0AF5] hover:bg-[#E8E6FE] rounded-md py-2 px-4 cursor-pointer",
                activeTab === "payment"
                  ? "text-[#1E0AF5] bg-[#E8E6FE]"
                  : "text-[#667085]"
              )}
            >
              Payment History
            </li>
            <li
              onClick={() => setActiveTab("transaction")}
              className={classNames(
                "block text-sm font-interSemiBold py-2 px-4 cursor-pointer hover:text-[#1E0AF5] hover:bg-[#E8E6FE] rounded-md",
                activeTab === "transaction"
                  ? "text-[#1E0AF5] bg-[#E8E6FE]"
                  : "text-[#667085]"
              )}
            >
              Transaction History
            </li>
          </ul>
        </div>
      </div>

      {/* Payment History Section */}
      {activeTab === "payment" && (
        <div className="border border-[#EAECF0] rounded-lg overflow-hidden max-h-[520px] overflow-y-auto">
          <table className="w-full table-fixed" cellPadding={1} border={0}>
            <thead className="bg-[#F9FAFB] border-b border-[#EAECF0] sticky top-0">
              <tr>
                <th className="w-1/5 text-left py-3 px-6 font-interMedium text-sm text-[#475467]">
                  <div className="flex items-center gap-x-2">
                    <span
                      className="cursor-pointer"
                      onClick={() => handlePaymentSort("date")}
                    >
                      Date
                    </span>
                    <img
                      className={`w-4 cursor-pointer transform ${
                        paymentSortParams.field === "date" &&
                        paymentSortParams.order === "asc"
                          ? "rotate-0"
                          : "rotate-180"
                      }`}
                      onClick={() => handlePaymentSort("date")}
                      src={allImgPaths.arrowUp3}
                      alt=""
                    />
                  </div>
                </th>
                <th className="w-1/3 text-left py-3 px-6 font-interMedium text-sm text-[#475467]">
                  <div className="flex items-center gap-x-2">
                    <span
                      className="cursor-pointer"
                      onClick={() => handlePaymentSort("invoice")}
                    >
                      Invoice
                    </span>
                    <img
                      className={`w-4 cursor-pointer transform ${
                        paymentSortParams.field === "invoice" &&
                        paymentSortParams.order === "asc"
                          ? "rotate-0"
                          : "rotate-180"
                      }`}
                      onClick={() => handlePaymentSort("invoice")}
                      src={allImgPaths.arrowUp3}
                      alt=""
                    />
                  </div>
                </th>
                <th className="w-1/5 text-left py-3 px-6 font-interMedium text-sm text-[#475467]">
                  <div className="flex items-center gap-x-2">
                    <span
                      className="cursor-pointer"
                      onClick={() => handlePaymentSort("amount")}
                    >
                      Amount
                    </span>
                    <img
                      className={`w-4 cursor-pointer transform ${
                        paymentSortParams.field === "amount" &&
                        paymentSortParams.order === "asc"
                          ? "rotate-0"
                          : "rotate-180"
                      }`}
                      onClick={() => handlePaymentSort("amount")}
                      src={allImgPaths.arrowUp3}
                      alt=""
                    />
                  </div>
                </th>
                <th className="w-1/5 text-left py-3 px-6 font-interMedium text-sm text-[#475467]">
                  <div className="flex items-center gap-x-2">
                    <span
                      className="cursor-pointer"
                      onClick={() => handlePaymentSort("status")}
                    >
                      Status
                    </span>
                    <img
                      className={`w-4 cursor-pointer transform ${
                        paymentSortParams.field === "status" &&
                        paymentSortParams.order === "asc"
                          ? "rotate-0"
                          : "rotate-180"
                      }`}
                      onClick={() => handlePaymentSort("status")}
                      src={allImgPaths.arrowUp3}
                      alt=""
                    />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {paymentLoading ? (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-[#475467]">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1751D0]"></div>
                    </div>
                  </td>
                </tr>
              ) : payments.length > 0 ? (
                payments.map((payment, index) => (
                  <tr
                    key={payment.id}
                    className={`border-b border-[#EAECF0] ${
                      index % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"
                    }`}
                  >
                    <td
                      className="w-1/5 py-4 px-6 text-sm text-[#475467] truncate"
                      title={payment.date}
                    >
                      {payment.date}
                    </td>
                    <td
                      className="w-1/3 py-4 px-6 text-sm text-[#475467] truncate"
                      title={payment.invoice}
                    >
                      {payment.invoice}
                    </td>
                    <td
                      className="w-1/5 py-4 px-6 text-sm text-[#475467] font-interMedium"
                      title={payment.amount}
                    >
                      {payment.amount}
                    </td>
                    <td
                      className="w-1/5 py-4 px-6 text-sm"
                      title={payment.status}
                    >
                      <span
                        className={`inline-block py-0.5 px-2.5 rounded-full ${
                          payment.status === "completed"
                            ? "bg-[#ECFDF3] text-[#027A48]"
                            : "bg-[#FEF3F2] text-[#B42318]"
                        }`}
                      >
                        {payment.status === "completed"
                          ? "Completed"
                          : "Failed"}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-[#475467]">
                    <div className="flex flex-col items-center justify-center">
                      <p className="font-interMedium text-base">
                        No payment history found
                      </p>
                      <p className="text-sm text-[#667085]">
                        There are no payments to display at this time.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Transaction History Section */}
      {activeTab === "transaction" && (
        <div className="border border-[#EAECF0] rounded-lg overflow-hidden max-h-[520px] overflow-y-auto">
          <table className="w-full table-fixed" cellPadding={1} border={0}>
            <thead className="bg-[#F9FAFB] border-b border-[#EAECF0] sticky top-0">
              <tr>
                <th className="w-[15%] text-left py-3 px-6 font-interMedium text-sm text-[#475467]">
                  <div className="flex items-center gap-x-2">
                    <span
                      className="cursor-pointer"
                      onClick={() => handleTransactionSort("date")}
                    >
                      Date
                    </span>
                    <img
                      className={`w-4 cursor-pointer transform ${
                        transactionSortParams.field === "date" &&
                        transactionSortParams.order === "asc"
                          ? "rotate-0"
                          : "rotate-180"
                      }`}
                      onClick={() => handleTransactionSort("date")}
                      src={allImgPaths.arrowUp3}
                      alt=""
                    />
                  </div>
                </th>
                <th className="w-[17%] text-left py-3 px-6 font-interMedium text-sm text-[#475467]">
                  <div className="flex items-center gap-x-2">
                    <span
                      className="cursor-pointer"
                      onClick={() => handleTransactionSort("project")}
                    >
                      Project
                    </span>
                    <img
                      className={`w-4 cursor-pointer transform ${
                        transactionSortParams.field === "project" &&
                        transactionSortParams.order === "asc"
                          ? "rotate-0"
                          : "rotate-180"
                      }`}
                      onClick={() => handleTransactionSort("project")}
                      src={allImgPaths.arrowUp3}
                      alt=""
                    />
                  </div>
                </th>
                <th className="w-[17%] text-left py-3 px-6 font-interMedium text-sm text-[#475467]">
                  <div className="flex items-center gap-x-2">
                    <span
                      className="cursor-pointer"
                      onClick={() => handleTransactionSort("activity")}
                    >
                      Activity
                    </span>
                    <img
                      className={`w-4 cursor-pointer transform ${
                        transactionSortParams.field === "activity" &&
                        transactionSortParams.order === "asc"
                          ? "rotate-0"
                          : "rotate-180"
                      }`}
                      onClick={() => handleTransactionSort("activity")}
                      src={allImgPaths.arrowUp3}
                      alt=""
                    />
                  </div>
                </th>
                <th className="w-[17%] text-left py-3 px-6 font-interMedium text-sm text-[#475467]">
                  <div className="flex items-center gap-x-2">
                    <span
                      className="cursor-pointer"
                      onClick={() => handleTransactionSort("name")}
                    >
                      Name
                    </span>
                    <img
                      className={`w-4 cursor-pointer transform ${
                        transactionSortParams.field === "name" &&
                        transactionSortParams.order === "asc"
                          ? "rotate-0"
                          : "rotate-180"
                      }`}
                      onClick={() => handleTransactionSort("name")}
                      src={allImgPaths.arrowUp3}
                      alt=""
                    />
                  </div>
                </th>
                <th className="w-[17%] text-left py-3 px-6 font-interMedium text-sm text-[#475467]">
                  <div className="flex items-center gap-x-2">
                    <span
                      className="cursor-pointer"
                      onClick={() => handleTransactionSort("teamMember")}
                    >
                      Team Member
                    </span>
                    <img
                      className={`w-4 cursor-pointer transform ${
                        transactionSortParams.field === "teamMember" &&
                        transactionSortParams.order === "asc"
                          ? "rotate-0"
                          : "rotate-180"
                      }`}
                      onClick={() => handleTransactionSort("teamMember")}
                      src={allImgPaths.arrowUp3}
                      alt=""
                    />
                  </div>
                </th>
                <th className="w-[17%] text-left py-3 px-6 font-interMedium text-sm text-[#475467]">
                  <div className="flex items-center gap-x-2">
                    <span
                      className="cursor-pointer"
                      onClick={() => handleTransactionSort("charge")}
                    >
                      Charge
                    </span>
                    <img
                      className={`w-4 cursor-pointer transform ${
                        transactionSortParams.field === "charge" &&
                        transactionSortParams.order === "asc"
                          ? "rotate-0"
                          : "rotate-180"
                      }`}
                      onClick={() => handleTransactionSort("charge")}
                      src={allImgPaths.arrowUp3}
                      alt=""
                    />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-[#475467]">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1751D0]"></div>
                    </div>
                  </td>
                </tr>
              ) : transactions.length > 0 ? (
                transactions.map((transaction, index) => (
                  <tr
                    key={transaction.id}
                    className={`border-b border-[#EAECF0] ${
                      index % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"
                    }`}
                  >
                    <td
                      className="w-[15%] py-4 px-6 text-sm text-[#475467] truncate"
                      title={transaction.date}
                    >
                      {transaction.date}
                    </td>
                    <td
                      className="w-[17%] py-4 px-6 text-sm text-[#475467] truncate"
                      title={transaction.project}
                    >
                      {transaction.project}
                    </td>
                    <td
                      className="w-[17%] py-4 px-6 text-sm text-[#475467] truncate"
                      title={transaction.activity}
                    >
                      {transaction.activity}
                    </td>
                    <td
                      className="w-[17%] py-4 px-6 text-sm text-[#475467] truncate"
                      title={transaction.name}
                    >
                      {transaction.name}
                    </td>
                    <td
                      className="w-[17%] py-4 px-6 text-sm text-[#475467] truncate"
                      title={transaction.teamMember}
                    >
                      {transaction.teamMember}
                    </td>
                    <td
                      className="w-[17%] py-4 px-6 text-sm text-[#475467] font-interMedium"
                      title={transaction.charge}
                    >
                      {transaction.charge}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-[#475467]">
                    <div className="flex flex-col items-center justify-center">
                      <p className="font-interMedium text-base">
                        No transactions found
                      </p>
                      <p className="text-sm text-[#667085]">
                        There are no transactions to display at this time.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Credit Modal */}
      <AddCreditModal
        isShow={isAddCreditModalOpen}
        setIsShow={setIsAddCreditModalOpen}
        teamId={currentTeamId}
        onSuccess={handleAddCreditSuccess}
      />
    </div>
  );
};

export default BillingTab;
